import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../features/auth/AuthContext'
import { apiFetch } from '../http/apiFetch'
import type { IntegrationTargetEntity } from '../../features/integrator/types'

type ProtectedEntity = {
  entity: IntegrationTargetEntity
  canCreate: boolean
  canDelete: boolean
  protectedFields: string[]
}
type ProtectionSnapshot = { companyId: string | null; entities: ProtectedEntity[] }

const cache = new Map<string, Promise<ProtectionSnapshot>>()

async function load(scope: string) {
  const existing = cache.get(scope)
  if (existing) return existing
  const request = apiFetch('/api/v1/integration-protection', { credentials: 'include' })
    .then(async response => {
      if (!response.ok) throw new Error('integrator.protectionLoadError')
      return response.json() as Promise<ProtectionSnapshot>
    })
  cache.set(scope, request)
  request.catch(() => cache.delete(scope))
  return request
}

export function useIntegrationProtection(entity: IntegrationTargetEntity) {
  const { user } = useAuth()
  const scope = `${user?.configurationId ?? ''}:${user?.activeCompanyId ?? ''}`
  const [snapshot, setSnapshot] = useState<ProtectionSnapshot | null>(null)
  useEffect(() => {
    let current = true
    if (!user) { setSnapshot(null); return () => { current = false } }
    void load(scope).then(value => { if (current) setSnapshot(value) })
      .catch(() => { if (current) setSnapshot(null) })
    return () => { current = false }
  }, [scope, user])
  return useMemo(() => {
    const item = snapshot?.entities.find(value => value.entity === entity)
    const fields = new Set(item?.protectedFields.map(value => value.toLocaleLowerCase()) ?? [])
    return {
      loading: Boolean(user) && snapshot === null,
      canCreate: item?.canCreate ?? true,
      canDelete: item?.canDelete ?? true,
      isProtected: (field: string) => fields.has(field.toLocaleLowerCase()),
    }
  }, [entity, snapshot, user])
}

export function clearIntegrationProtectionCache() { cache.clear() }
